import type { Brand } from './types';

export const DEFAULT_BRAND_ID = 'livewire';

export function brandId(): string {
  return import.meta.env.VITE_BRAND || DEFAULT_BRAND_ID;
}

/** URL of a file inside the brand folder, relative to the deployed base. */
export function brandAsset(brand: Pick<Brand, 'id'>, file: string): string {
  return `${import.meta.env.BASE_URL}brand/${brand.id}/${file}`;
}

export async function loadBrand(id: string = brandId()): Promise<Brand> {
  const res = await fetch(brandAsset({ id }, 'brand.json'));
  if (!res.ok) throw new Error(`brand.json for "${id}" failed to load (${res.status})`);
  const brand = (await res.json()) as Brand;
  if (brand.id !== id || !brand.colors?.accent || !brand.uniforms?.home) {
    throw new Error(`brand.json for "${id}" is missing required fields`);
  }
  applyBrandCss(brand);
  return brand;
}

/** Exposes brand colours to CSS so no stylesheet hardcodes them. */
export function applyBrandCss(brand: Brand): void {
  const root = document.documentElement.style;
  for (const [key, value] of Object.entries(brand.colors)) {
    root.setProperty(`--brand-${key.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())}`, value);
  }
  document.title = brand.title ? `${brand.name} ${brand.title.lines.map((l) => l.text).join(' ')}` : `${brand.name} Gridiron`;
}

/** Loads the static logo SVG as an image, for drawing into generated textures. */
export function loadBrandImage(brand: Brand, file: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Brand image ${file} failed to load`));
    img.src = brandAsset(brand, file);
  });
}

/**
 * Loads a brand SVG as markup to place in the page (so its own animation runs and it takes the CSS colour):
 * scripts, embedded HTML and event attributes are dropped, the root fill becomes currentColor and its fixed
 * size goes (CSS sizes it). Null if it fails to load or parse.
 */
export async function loadBrandSvg(brand: Brand, file: string): Promise<SVGSVGElement | null> {
  try {
    const res = await fetch(brandAsset(brand, file));
    if (!res.ok) return null;
    const doc = new DOMParser().parseFromString(await res.text(), 'image/svg+xml');
    const svg = doc.documentElement;
    if (!(svg instanceof SVGSVGElement)) return null;
    for (const bad of svg.querySelectorAll('script, foreignObject')) bad.remove();
    for (const node of svg.querySelectorAll('*')) {
      for (const attr of [...node.attributes]) if (/^on/i.test(attr.name) || /^\s*javascript:/i.test(attr.value)) node.removeAttribute(attr.name);
    }
    svg.setAttribute('fill', 'currentColor');
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    return document.importNode(svg, true);
  } catch {
    return null;
  }
}
